/**
 * Website › Pages editing (PageEditor.tsx, backlog 8ffdec3d): a text block's
 * body is stored as HTML; the editor shows it as plain paragraphs with
 * **bold**, *italic* and [links](https://…), which the server turns back into
 * HTML on save (pageBlocks.js markdown()). Anything richer — lists, headings,
 * images, video — can't round-trip through a plain box, so that body is left
 * for the agent (editableText returns null).
 */

const SIMPLE_TAGS = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'a']);

const decodeEntities = (s: string) =>
  s
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, '&');

/** The body as the editor's plain text, or null when it has more than the editor can show. */
export function editableText(html?: string | null): string | null {
  const src = String(html || '').trim();
  if (!src) return '';
  for (const m of Array.from(src.matchAll(/<\/?([a-z0-9]+)\b[^>]*>/gi))) {
    if (!SIMPLE_TAGS.has(m[1].toLowerCase())) return null;
  }
  const md = src
    .replace(
      /<a\b[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi,
      (_m, href, text) => `[${text}](${href})`
    )
    .replace(/<\/?(strong|b)\b[^>]*>/gi, '**')
    .replace(/<\/?(em|i)\b[^>]*>/gi, '*')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p\b[^>]*>/gi, '\n\n')
    .replace(/<\/?p\b[^>]*>/gi, '');
  if (/<[a-z/]/i.test(md)) return null;
  return decodeEntities(md).trim();
}

/** "Hero", "Article list", "Call to action": a block type as the editor names it. */
export const BLOCK_NAMES: Record<string, string> = {
  hero: 'Hero',
  articles: 'Article list',
  text: 'Text',
  quote: 'Quote',
  cta: 'Call to action',
  form: 'Form',
  features: 'Features',
  columns: 'Columns',
  image: 'Image',
  faq: 'Questions',
  table: 'Table',
  video: 'Video',
  embed: 'Embed',
  html: 'Custom HTML',
};

export const blockName = (type: string) =>
  BLOCK_NAMES[type] || type.charAt(0).toUpperCase() + type.slice(1);

const SUMMARY_MAX = 48;
const plainText = (v: unknown) =>
  typeof v === 'string' ? v.replace(/\*/g, '').replace(/\s+/g, ' ').trim() : '';
const itemTitle = (x: any) =>
  plainText(x?.title) || plainText(x?.heading) || plainText(x?.question) || plainText(x?.name);

/**
 * What tells one block from another of its type (Round 34 B, backlog
 * f00d07bd): its heading; else its HTML or embed label, or the image's alt;
 * else its first three items' titles. Stars (italics) out, cut at 48.
 */
export function blockSummary(b: { type: string; [key: string]: any }): string {
  let s = plainText(b.heading) || plainText(b.title);
  if (!s) s = plainText(b.label) || plainText(b.image?.alt) || plainText(b.alt);
  if (!s) {
    const lists = [b.items, b.tiers, b.questions, b.plans].filter(Array.isArray);
    let titles: string[] = lists.flatMap((l: any[]) => l.map(itemTitle)).filter(Boolean);
    // A Columns block: its columns' own blocks.
    if (!titles.length && Array.isArray(b.columns)) {
      titles = b.columns
        .flatMap((c: any) => (Array.isArray(c?.blocks) ? c.blocks : [c]))
        .map((x: any) => plainText(x?.heading) || itemTitle(x))
        .filter(Boolean);
    }
    s = titles.slice(0, 3).join(', ');
  }
  return s.length > SUMMARY_MAX ? `${s.slice(0, SUMMARY_MAX - 1).trimEnd()}…` : s;
}

/** "Features · How it works", or just "Features" when there's nothing to tell. */
export function blockLabel(b: { type: string; [key: string]: any }): string {
  const summary = blockSummary(b);
  return summary ? `${blockName(b.type)} · ${summary}` : blockName(b.type);
}

/** What Ask your agent copies for a block it edits (Round 34 B). */
export function blockPrompt(
  page: { title: string; path: string },
  b: { type: string; [key: string]: any },
  n: number,
  total: number
): string {
  const summary = blockSummary(b);
  const which = summary
    ? `the ${blockName(b.type)} block “${summary}”`
    : `the ${blockName(b.type)} block`;
  return `On the ${page.title || 'untitled'} page (${page.path || '/'}), change block ${n} of ${total}, ${which}. Here's what I want: `;
}

/** A page address as typed: "/about-us" or "/docs/setup" → { slug, parent }; null when it isn't one. */
export function parseAddress(value: string): { slug: string; parent: string | null } | null {
  const v = value.trim();
  if (!/^\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)?$/.test(v)) return null;
  const [first, second] = v.slice(1).split('/');
  return second ? { slug: second, parent: first } : { slug: first, parent: null };
}

/** What's wrong with an address as typed, in words; '' when it's fine. */
export function addressProblem(value: string, taken: Set<string>): string {
  const v = value.trim();
  if (!v.startsWith('/')) return 'Start the address with /.';
  if (v === '/') return 'Only your front page lives at /.';
  if (!parseAddress(v)) return 'Use lowercase letters, numbers and dashes.';
  if (taken.has(v)) return 'Another page already has this address.';
  return '';
}

/** "n of 160 characters". */
export const DESCRIPTION_MAX = 160;

/**
 * *Emphasis* in a block heading, as the site draws it (smagent-sites
 * lib/emphasis.tsx): paired stars around something that isn't only spaces
 * become italics; a lone "*" stays as typed.
 */
const EMPHASIS = /\*([^*\n]*[^*\s][^*\n]*)\*/;
export const hasEmphasis = (text: string) => EMPHASIS.test(text);

/**
 * The heading's I button (and ⌘I): the selected words in or out of *stars*.
 * A selection already inside stars, or one that carries its own, loses them;
 * otherwise it's wrapped, leaving any spaces at its edges outside. With
 * nothing selected, inside an italic run unwraps the run, elsewhere a "**"
 * pair goes in with the cursor between. Returns the new text and selection.
 */
export function toggleItalics(
  value: string,
  start: number,
  end: number
): { value: string; start: number; end: number } {
  const [s, e] = start <= end ? [start, end] : [end, start];
  if (s === e) {
    for (const m of Array.from(value.matchAll(new RegExp(EMPHASIS.source, 'g')))) {
      const from = m.index ?? 0;
      const to = from + m[0].length;
      if (s > from && s < to) {
        return {
          value: value.slice(0, from) + m[1] + value.slice(to),
          start: s - 1,
          end: s - 1,
        };
      }
    }
    return { value: `${value.slice(0, s)}**${value.slice(s)}`, start: s + 1, end: s + 1 };
  }
  if (value[s - 1] === '*' && value[e] === '*') {
    return {
      value: value.slice(0, s - 1) + value.slice(s, e) + value.slice(e + 1),
      start: s - 1,
      end: e - 1,
    };
  }
  const picked = value.slice(s, e);
  if (picked.includes('*')) {
    const plain = picked.replace(/\*/g, '');
    return { value: value.slice(0, s) + plain + value.slice(e), start: s, end: s + plain.length };
  }
  const lead = picked.length - picked.trimStart().length;
  const trail = picked.length - picked.trimEnd().length;
  const word = picked.trim();
  if (!word) return { value, start: s, end: e };
  const a = s + lead;
  const b = e - trail;
  return {
    value: `${value.slice(0, a)}*${word}*${value.slice(b)}`,
    start: a + 1,
    end: b + 1,
  };
}

/** Where a footer link sits: the bottom row (column null) or a column. */
type FooterSpot = { column: number | null; index: number; href: string; label: string };

/** "/About/" and "/about" are the same place; "/about#team" isn't. */
const sameHref = (href: string) => {
  const h = href.trim().toLowerCase().split('?')[0];
  return h.length > 1 ? h.replace(/\/+(#|$)/, '$1') : h;
};

/**
 * The amber note on footer links that go to the same address as another
 * footer link (Website Pages Editing dev spec, Footer › Duplicate hint; QA
 * round 30, 72092c50): "Also in the bottom row. Readers see it twice." A
 * warning only. Links with no label yet (a fresh "+ Add link") are left out.
 */
export function footerDuplicates(
  bottom: Array<{ label: string; href: string }>,
  columns: Array<{ heading: string; links: Array<{ label: string; href: string }> }>
): { links: Record<number, string>; columns: Record<number, Record<number, string>> } {
  const spots: FooterSpot[] = [
    ...bottom.map((l, index) => ({ column: null, index, href: l.href, label: l.label })),
    ...columns.flatMap((c, column) =>
      c.links.map((l, index) => ({ column, index, href: l.href, label: l.label }))
    ),
  ].filter((s) => s.label.trim() && s.href.trim());
  const out = {
    links: {} as Record<number, string>,
    columns: {} as Record<number, Record<number, string>>,
  };
  const where = (s: FooterSpot, from: FooterSpot) => {
    if (s.column === null) return 'the bottom row';
    if (s.column === from.column) return 'this column';
    const heading = columns[s.column].heading.trim();
    return heading ? `the ${heading} column` : `column ${s.column + 1}`;
  };
  for (const s of spots) {
    const others = spots.filter((o) => o !== s && sameHref(o.href) === sameHref(s.href));
    if (!others.length) continue;
    const names = Array.from(new Set(others.map((o) => where(o, s))));
    const list =
      names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    const n = others.length + 1;
    const note = `Also in ${list}. Readers see it ${n === 2 ? 'twice' : `${n} times`}.`;
    if (s.column === null) out.links[s.index] = note;
    else (out.columns[s.column] ||= {})[s.index] = note;
  }
  return out;
}

/** A link that leaves the site ("https://…"), picked with "Web address…". */
export const isWebAddress = (href: string) => /^https?:/i.test(href.trim());

/**
 * What's wrong with a typed web address ('' when fine, or when the link isn't
 * one): the dev spec's "Web address in every Goes to picker" (446d9c6d).
 */
export function webAddressProblem(href: string): string {
  if (!isWebAddress(href)) return '';
  try {
    const u = new URL(href.trim());
    if (/^https?:$/.test(u.protocol) && /^[^.\s]+(\.[^.\s]+)+$/.test(u.hostname)) return '';
  } catch {
    // Not a URL at all: same message.
  }
  return 'Web addresses start with https:// and include a domain, like https://example.com.';
}

/**
 * Reader questions use the Ask page ("Ask page" or "Both", switched on and
 * allowed): the site adds "Ask AI" at the end of the header menu
 * (smagent-sites Chrome.tsx hasAskPage).
 */
export const hasAskPage = (
  chat:
    | { eligible?: boolean | null; enabled?: boolean | null; mode?: string | null }
    | null
    | undefined
) => Boolean(chat?.eligible && chat.enabled && (chat.mode === 'page' || chat.mode === 'both'));
